/**
 * Saving Performance Dashboard (Kinerja Simpanan)
 */

import m from "mithril";
import { getMithril } from '../utils.js';

export default {
    oninit(vnode) {
        const today = new Date().toISOString().slice(0, 10);
        vnode.state.startDate = today;
        vnode.state.endDate = today;
        vnode.state.productsData = [];
        vnode.state.isLoading = false;
        vnode.state.error = null;
        vnode.state.expandedProducts = {};
        vnode.state.expandedActors = {};

        this.fetchPerformanceData(vnode);
    },

    async fetchPerformanceData(vnode) {
        vnode.state.isLoading = true;
        vnode.state.error = null;
        try {
            const data = await getMithril(`/api/savings-performance?startDate=${vnode.state.startDate}&endDate=${vnode.state.endDate}`);
            if (data && data.success) {
                vnode.state.productsData = data.data;
            } else {
                vnode.state.error = data?.error || 'Failed to load performance data';
            }
        } catch (err) {
            vnode.state.error = err.message || 'System error loading data';
        } finally {
            vnode.state.isLoading = false;
            m.redraw();
        }
    },

    toggleProduct(vnode, productId) {
        vnode.state.expandedProducts[productId] = !vnode.state.expandedProducts[productId];
    },

    toggleActorCat(vnode, uniqueKey) {
        vnode.state.expandedActors[uniqueKey] = !vnode.state.expandedActors[uniqueKey];
    },

    toggleActorTransactions(vnode, actorKey) {
        vnode.state.expandedActorTransactions = vnode.state.expandedActorTransactions || {};
        vnode.state.expandedActorTransactions[actorKey] = !vnode.state.expandedActorTransactions[actorKey];
    },

    formatIDR(value) {
        return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(value || 0);
    },

    formatDate(dateStr) {
        if (!dateStr) return "-";
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return "-";
        return d.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
    },

    renderActorsAccordion(vnode, title, dataArray, uniquePrefix) {
        if (!dataArray || dataArray.length === 0) return null;

        const sum = dataArray.reduce((acc, curr) => acc + curr.totalAmount, 0);

        return m(".mb-4.border.border-gray-200.dark:border-gray-700.rounded-lg.overflow-hidden", [
            m("button.w-full.flex.items-center.justify-between.p-4.bg-gray-50.dark:bg-gray-800.hover:bg-gray-100.dark:hover:bg-gray-700.transition-colors", {
                onclick: () => this.toggleActorCat(vnode, uniquePrefix)
            }, [
                m("div.flex.items-center", [
                    m("i.ri-group-line.text-lg.text-indigo-500.mr-2"),
                    m("span.font-medium.text-gray-900.dark:text-white", title)
                ]),
                m("div.flex.items-center", [
                    m("span.font-semibold.text-gray-900.dark:text-white.mr-4", this.formatIDR(sum)),
                    m(`i.ri-arrow-${vnode.state.expandedActors[uniquePrefix] ? 'up' : 'down'}-s-line.text-gray-500`)
                ])
            ]),
            vnode.state.expandedActors[uniquePrefix] && m("div.p-4.bg-white.dark:bg-gray-900", [
                m("ul.space-y-3", dataArray.map((actor, idx) => {
                    const actorKey = `${uniquePrefix}_${idx}`;
                    const isTxExpanded = vnode.state.expandedActorTransactions?.[actorKey];

                    return m("li.flex.flex-col.bg-gray-50.dark:bg-gray-800.rounded-md.overflow-hidden", [
                        m("div.flex.justify-between.items-center.p-3.cursor-pointer.hover:bg-gray-100.dark:hover:bg-gray-750.transition-colors", {
                            onclick: () => this.toggleActorTransactions(vnode, actorKey)
                        }, [
                            m("div", [
                                m("p.text-sm.font-medium.text-gray-900.dark:text-white", actor.name),
                                m("p.text-xs.text-gray-500.capitalize", actor.role),
                            ]),
                            m("div.flex.items-center.text-right", [
                                m("div.mr-4", [
                                    m("p.text-sm.font-bold.text-gray-900.dark:text-white", this.formatIDR(actor.totalAmount)),
                                    m("p.text-xs.text-gray-500", `${actor.transactionCount} Transaksi`),
                                ]),
                                m(`i.ri-arrow-${isTxExpanded ? 'up' : 'down'}-s-line.text-gray-400`)
                            ])
                        ]),
                        isTxExpanded && m("div.p-3.bg-white.dark:bg-gray-900.border-t.border-gray-100.dark:border-gray-700", [
                            m("table.w-full.text-xs", [
                                m("thead", m("tr.text-gray-400.border-b.border-gray-100.dark:border-gray-700", [
                                    m("th.text-left.py-2.font-normal", "Tgl"),
                                    m("th.text-left.py-2.font-normal", "No Reff"),
                                    m("th.text-left.py-2.font-normal", "Keterangan"),
                                    m("th.text-right.py-2.font-normal", "Jumlah")
                                ])),
                                m("tbody", actor.transactions.map(tx => m("tr.border-b.border-gray-50.dark:border-gray-800/50.hover:bg-gray-50.dark:hover:bg-gray-800/50", [
                                    m("td.py-2.text-gray-600.dark:text-gray-400.whitespace-nowrap", this.formatDate(tx.tgl)),
                                    m("td.py-2.text-gray-900.dark:text-white.font-mono", tx.no),
                                    m("td.py-2.text-gray-500.dark:text-gray-400", tx.deskripsi || tx.type),
                                    m("td.py-2.text-right.font-semibold.text-gray-900.dark:text-white", this.formatIDR(tx.amount))
                                ])))
                            ])
                        ])
                    ])
                }))
            ])
        ]);
    },

    view(vnode) {
        const isRange = vnode.state.startDate !== vnode.state.endDate;
        const periodLabel = isRange ? "Periode" : "Harian";

        return m("div.p-6.max-w-7xl.mx-auto", [
            // Header Section
            m("div.flex.flex-col.lg:flex-row.justify-between.items-start.lg:items-center.mb-8", [
                m("div", [
                    m("h1.text-2xl.font-bold.text-gray-900.dark:text-white", "Kinerja Simpanan"),
                    m("p.text-gray-600.dark:text-gray-400.mt-1", `Agregasi transaksi simpanan ${periodLabel.toLowerCase()} berdasar petugas`)
                ]),
                m("div.mt-4.lg:mt-0.flex.flex-wrap.items-center.gap-4", [
                    m("div.flex.items-center.gap-2", [
                        m("div.relative", [
                            m("input[type=date].pl-10.pr-4.py-2.border.border-gray-300.dark:border-gray-600.rounded-lg.bg-white.dark:bg-gray-800.text-gray-900.dark:text-white.focus:ring-2.focus:ring-indigo-500", {
                                value: vnode.state.startDate,
                                onchange: (e) => {
                                    vnode.state.startDate = e.target.value;
                                    this.fetchPerformanceData(vnode);
                                }
                            }),
                            m("i.ri-calendar-line.absolute.left-3.top-2_5.text-gray-400")
                        ]),
                        m("span.text-gray-500.dark:text-gray-400", "-"),
                        m("div.relative", [
                            m("input[type=date].pl-10.pr-4.py-2.border.border-gray-300.dark:border-gray-600.rounded-lg.bg-white.dark:bg-gray-800.text-gray-900.dark:text-white.focus:ring-2.focus:ring-indigo-500", {
                                value: vnode.state.endDate,
                                onchange: (e) => {
                                    vnode.state.endDate = e.target.value;
                                    this.fetchPerformanceData(vnode);
                                }
                            }),
                            m("i.ri-calendar-line.absolute.left-3.top-2_5.text-gray-400")
                        ]),
                    ]),
                    m("button.px-4.py-2.bg-indigo-600.text-white.rounded-lg.hover:bg-indigo-700.transition-colors.shadow-sm.flex.items-center", {
                        onclick: () => this.fetchPerformanceData(vnode)
                    }, [
                        m("i.ri-refresh-line.mr-2"),
                        "Refresh"
                    ])
                ])
            ]),

            vnode.state.error && m("div.p-4.mb-6.bg-red-50.dark:bg-red-900/30.text-red-600.dark:text-red-400.rounded-lg.border.border-red-200.dark:border-red-800.flex.items-center", [
                m("i.ri-error-warning-line.mr-2.text-lg"),
                vnode.state.error
            ]),

            vnode.state.isLoading ? m("div.py-12.flex.flex-col.items-center.justify-center", [
                m("div.animate-spin.rounded-full.h-12.w-12.border-b-2.border-indigo-600.mb-4"),
                m("p.text-gray-500.dark:text-gray-400", "Mengambil data rekap...")
            ]) :
                // Main Product Listing
                m("div.space-y-6", vnode.state.productsData.map(product => {
                    const isExpanded = vnode.state.expandedProducts[product.id];

                    return m("div.bg-white.dark:bg-gray-800.rounded-xl.shadow-sm.border.border-gray-200.dark:border-gray-700.overflow-hidden.transition-all.duration-200", [
                        // Card Header (Click to expand)
                        m("div.p-6.cursor-pointer.hover:bg-gray-50.dark:hover:bg-gray-750.transition-colors", {
                            onclick: () => this.toggleProduct(vnode, product.id)
                        }, [
                            m("div.flex.justify-between.items-center.mb-4", [
                                m("div.flex.items-center", [
                                    m("div.w-10.h-10.rounded-full.bg-indigo-100.dark:bg-indigo-900/50.flex.items-center.justify-center.mr-4", [
                                        m("i.ri-wallet-3-line.text-xl.text-indigo-600.dark:text-indigo-400")
                                    ]),
                                    m("div", [
                                        m("h3.text-lg.font-bold.text-gray-900.dark:text-white", product.name),
                                        m("p.text-xs.font-medium.text-gray-500.uppercase.tracking-wide", product.code)
                                    ])
                                ]),
                                m("div.text-right.flex.items-center", [
                                    m("div.mr-4", [
                                        m("p.text-xs.text-gray-500.dark:text-gray-400", `Saldo Akhir ${periodLabel}`),
                                        m("p.text-xl.font-black.text-gray-900.dark:text-white", this.formatIDR(product.todayBalance))
                                    ]),
                                    m("div.w-8.h-8.rounded-full.bg-gray-100.dark:bg-gray-700.flex.items-center.justify-center", [
                                        m(`i.ri-arrow-${isExpanded ? 'up' : 'down'}-s-line.text-gray-600.dark:text-gray-400`)
                                    ])
                                ])
                            ]),

                            // Metrics Summary Bar (Visible even when collapsed)
                            m("div.grid.grid-cols-2.md:grid-cols-4.gap-4.pt-4.border-t.border-gray-100.dark:border-gray-700", [
                                m("div", [
                                    m("p.text-xs.text-gray-500.mb-1", "Saldo Sebelumnya"),
                                    m("p.font-semibold.text-gray-700.dark:text-gray-300", this.formatIDR(product.previousBalance))
                                ]),
                                m("div", [
                                    m("p.text-xs.text-emerald-600.mb-1.flex.items-center", [m("i.ri-arrow-right-down-line.mr-1"), "Pemasukan"]),
                                    m("p.font-bold.text-emerald-600.dark:text-emerald-400", this.formatIDR(product.deposits.reduce((sum, item) => sum + item.totalAmount, 0)))
                                ]),
                                m("div", [
                                    m("p.text-xs.text-rose-600.mb-1.flex.items-center", [m("i.ri-arrow-right-up-line.mr-1"), "Penarikan"]),
                                    m("p.font-bold.text-rose-600.dark:text-rose-400", this.formatIDR(product.withdrawals.reduce((sum, item) => sum + item.totalAmount, 0)))
                                ]),
                                m("div", [
                                    m("p.text-xs.text-blue-600.mb-1", "Perubahan dari Sistem"),
                                    m("div.flex.flex-col", [
                                        m("p.font-bold.text-blue-600.dark:text-blue-400", this.formatIDR(
                                            (product.systemBreakdown?.interest || 0) + 
                                            (product.systemBreakdown?.autoDebit || 0) + 
                                            (product.systemBreakdown?.mgmt || 0)
                                        )),
                                        (product.systemBreakdown?.interest > 0 || product.systemBreakdown?.autoDebit > 0) && m("p.text-[10px].text-blue-500.leading-tight", 
                                            `${product.systemBreakdown?.interest > 0 ? 'Bunga ' : ''}${product.systemBreakdown?.autoDebit > 0 ? '& Auto-Debet' : ''}`
                                        )
                                    ])
                                ])
                            ])
                        ]),

                        // Expanded Detail
                        isExpanded && m("div.p-6.pt-0.bg-gray-50/50.dark:bg-gray-800/50.border-t.border-gray-100.dark:border-gray-700", [
                            m("h4.text-sm.font-bold.text-gray-900.dark:text-white.mb-4.pt-6", `Rincian Transaksi ${periodLabel} (Berdasarkan Petugas)`),

                            m("div.grid.grid-cols-1.lg:grid-cols-2.gap-6", [
                                // Column 1: Activities that INCREASE balance
                                m("div", [
                                    this.renderActorsAccordion(vnode, "Total Setoran", product.deposits, `dep_${product.id}`),
                                    product.systemBreakdown?.interest > 0 && this.renderActorsAccordion(vnode, "Bunga Otomatis Sistem", product.otherAdjustments.filter(a => a.interestAmount > 0), `int_${product.id}`),
                                    product.systemBreakdown?.autoDebit > 0 && this.renderActorsAccordion(vnode, "Debet Otomatis (AD)", product.otherAdjustments.filter(a => a.autoDebitAmount > 0), `ad_${product.id}`)
                                ]),
                                // Column 2: Activities that DECREASE or ADJUST balance
                                m("div", [
                                    this.renderActorsAccordion(vnode, "Total Penarikan", product.withdrawals, `wd_${product.id}`),
                                    this.renderActorsAccordion(vnode, "Aksi Manajer / Kasir", product.changes.filter(a => ['manajer', 'kasir'].includes(a.role)), `mgmt_${product.id}`),
                                    this.renderActorsAccordion(vnode, "Koreksi / Penyesuaian Lain", product.changes.filter(a => !['manajer', 'kasir'].includes(a.role)), `chg_${product.id}`)
                                ])
                            ])
                        ])
                    ]);
                }))
        ]);
    }

};
